import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, Mail, MessageCircle, Check, X } from "lucide-react";
import { API, auth, ghostBtn, primaryBtn, handleAuthError, fmtDate } from "./shared";

const AdminVehicleAlert = ({ token, logout, vehicle: v, onClose }) => {
  const [targets, setTargets] = useState(null);
  const [sending, setSending] = useState(false);
  const load = () => axios.get(`${API}/vehicles/${v.id}/alert-targets`, auth(token)).then((r) => setTargets(r.data)).catch((err) => handleAuthError(err, logout, () => toast.error("Chargement impossible")));
  useEffect(() => { load(); }, [v.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingEmails = (targets || []).filter((t) => t.email && !t.alerted_at).length;
  const sendEmails = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/vehicles/${v.id}/alert`, {}, auth(token));
      setTargets(r.data.targets);
      toast.success(`${r.data.emailed} e-mail(s) envoyé(s)${r.data.failed ? `, ${r.data.failed} échec(s)` : ""}`);
    } catch (err) { handleAuthError(err, logout, () => toast.error(err.response?.data?.detail || "Envoi impossible")); } finally { setSending(false); }
  };
  const openWhatsApp = async (t) => {
    window.open(t.whatsapp_url, "_blank", "noopener");
    try { await axios.post(`${API}/vehicles/${v.id}/alert/${t.lead_id}/mark`, {}, auth(token)); load(); } catch { /* ignore */ }
  };

  return (
    <div className="mt-3 rounded-2xl border border-gold/40 bg-cream/40 p-5" data-testid={`vehicle-alert-panel`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display flex items-center gap-2 font-bold text-forest"><BellRing className="h-4 w-4 text-gold" /> Prévenir les clients intéressés · {v.title}</h3>
        <button type="button" onClick={onClose} data-testid="vehicle-alert-close" className={ghostBtn}><X className="h-3.5 w-3.5" /> Fermer</button>
      </div>
      <p className="mt-1 text-xs text-brand-hover">Clients ayant demandé un véhicule similaire (même type et même marque, ou prix à ±30 %), non clos, jamais prévenus pour cette annonce.</p>
      {targets === null ? <p className="mt-4 text-sm text-brand">Recherche…</p> : targets.length === 0 ? (
        <p className="mt-4 rounded-xl bg-white p-4 text-sm text-brand" data-testid="vehicle-alert-empty">Aucun client intéressé par un véhicule similaire pour le moment.</p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" onClick={sendEmails} disabled={sending || pendingEmails === 0} data-testid="vehicle-alert-send-emails" className={primaryBtn}><Mail className="h-4 w-4" /> {sending ? "Envoi…" : `Envoyer les e-mails (${pendingEmails})`}</button>
            <span className="text-xs text-brand-hover">Les clients sans e-mail sont à prévenir par WhatsApp ci-dessous (un clic = message prérempli).</span>
          </div>
          <div className="mt-4 space-y-2">
            {targets.map((t, i) => (
              <div key={t.lead_id} data-testid={`vehicle-alert-target-${i}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white px-4 py-3">
                <div className="min-w-0 text-sm">
                  <p className="font-semibold text-forest">{t.name} <span className="font-normal text-brand">· {t.phone}{t.email ? ` · ${t.email}` : ""}</span></p>
                  <p className="text-xs text-brand-hover">Intéressé par : {t.asked_title}</p>
                </div>
                {t.alerted_at ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-mint px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-brand" data-testid={`vehicle-alert-target-${i}-done`}><Check className="h-3 w-3" /> Prévenu par {t.channel === "email" ? "e-mail" : "WhatsApp"} · {fmtDate(t.alerted_at)}</span>
                ) : (
                  <div className="flex items-center gap-2">
                    {t.email && <span className="font-mono text-[10px] uppercase tracking-widest text-gold">E-mail en attente</span>}
                    {t.whatsapp_url && <button type="button" onClick={() => openWhatsApp(t)} data-testid={`vehicle-alert-target-${i}-whatsapp`} className={ghostBtn}><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminVehicleAlert;
